import { MONTHS } from '@/constants/constants'
import {
  PAGE_TEMPLATE_META_KEY,
  PAGE_TEMPLATE_META_VALUE,
  WXR_VERSION,
} from './constants'
import { getPageSlug, getSeoExcerpt, getSeoPostMeta } from './seo'
import type {
  WxrExportProjectData,
  WxrPageItem,
  WxrPostMetaItem,
} from './types'

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const ROOT_POST_PARENT = '0'

const pad = (value: number): string => String(value).padStart(2, '0')

const getCdata = (value: string): string =>
  `<![CDATA[${value.replace(/]]>/g, ']]]]><![CDATA[>')}]]>`

const getExportDate = (updatedAt: string | null): Date => {
  const date = new Date(updatedAt ?? '')

  return Number.isNaN(date.getTime()) ? new Date() : date
}

// Both dates go into GMT fields (`wp:post_date_gmt`, `pubDate`), so they are
// rendered in UTC. Local time here reads as the future to WordPress for anyone
// east of GMT, and the importer schedules such pages instead of publishing them.
// YYYY-MM-DD HH:MM:SS
const getPageDate = (date: Date): string =>
  `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate()
  )} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(
    date.getUTCSeconds()
  )}`

// Wed, 26 Jun 2024 08:47:16
const getPubDate = (date: Date): string =>
  `${WEEK_DAYS[date.getUTCDay()]}, ${pad(date.getUTCDate())} ${
    MONTHS[date.getUTCMonth()]
  } ${date.getUTCFullYear()} ${pad(date.getUTCHours())}:${pad(
    date.getUTCMinutes()
  )}:${pad(date.getUTCSeconds())}`

const getUniqueValue = (value: string, takenValues: Set<string>): string => {
  if (!takenValues.has(value)) {
    return value
  }

  let index = 2

  while (takenValues.has(`${value}-${index}`)) {
    index += 1
  }

  return `${value}-${index}`
}

const getPostIdsByPageId = (pages: WxrPageItem[]): Map<string, string> =>
  new Map(pages.map((page, index) => [String(page.id), String(index + 1)]))

// Домашняя страница сайтмапа — первая карточка выгрузки, у которой нет
// родителя внутри неё: именно она становится главной страницей сайта. В
// WordPress верхние страницы лежат рядом с главной, а не под ней, поэтому по
// умолчанию её прямых детей поднимаем в корень, а вложенность глубже
// сохраняем.
const getHomePageId = (
  pages: WxrPageItem[],
  hasParentInExport: (page: WxrPageItem) => boolean
): string | null => {
  const home = pages.find((page) => !hasParentInExport(page))

  return home ? String(home.id) : null
}

// Импортёр WordPress пропускает запись, если пара «заголовок + дата» уже есть,
// а дата у всех страниц файла одна — одинаковые заголовки просто потерялись бы
// при импорте. Поэтому заголовки разводим, но в слаг этот суффикс не пускаем.
const getTitlesByPageId = (pages: WxrPageItem[]): Map<string, string> => {
  const takenTitles = new Set<string>()

  return pages.reduce((titles, page, index) => {
    const title = getUniqueValue(
      page.title.trim() || `Page ${index + 1}`,
      takenTitles
    )

    takenTitles.add(title)
    titles.set(String(page.id), title)

    return titles
  }, new Map<string, string>())
}

// Слаг обязан быть уникальным только среди детей одной страницы, поэтому
// считаем по итоговому post_parent, а не по исходному parentId: у страниц, чей
// родитель в выгрузку не попал, post_parent схлопывается в корень.
const getSlugsByPageId = (
  pages: WxrPageItem[],
  withSeo: boolean,
  getPostParent: (page: WxrPageItem) => string
): Map<string, string> => {
  const takenSlugsByPostParent = new Map<string, Set<string>>()

  return pages.reduce((slugs, page, index) => {
    const postParent = getPostParent(page)
    const takenSlugs =
      takenSlugsByPostParent.get(postParent) ?? new Set<string>()
    const slug = getUniqueValue(
      getPageSlug(page, withSeo) || `page-${index + 1}`,
      takenSlugs
    )

    takenSlugs.add(slug)
    takenSlugsByPostParent.set(postParent, takenSlugs)
    slugs.set(String(page.id), slug)

    return slugs
  }, new Map<string, string>())
}

const getPostMetaXml = (items: WxrPostMetaItem[]): string =>
  items
    .map(({ key, value }) =>
      [
        '<wp:postmeta>',
        ` <wp:meta_key>${getCdata(key)}</wp:meta_key>`,
        ` <wp:meta_value>${getCdata(value)}</wp:meta_value>`,
        '</wp:postmeta>',
      ].join('')
    )
    .join('')

export const getProjectWxr = ({
  projectTitle,
  updatedAt,
  authorEmail,
  pages,
  withSeo,
  nestUnderHome,
}: WxrExportProjectData): string => {
  const date = getExportDate(updatedAt)
  const pageDate = getPageDate(date)
  const pubDate = getPubDate(date)
  const postIdsByPageId = getPostIdsByPageId(pages)
  const getParentPostId = (page: WxrPageItem): string | undefined =>
    postIdsByPageId.get(String(page.parentId ?? ''))
  const homePageId = nestUnderHome
    ? null
    : getHomePageId(pages, (page) => getParentPostId(page) !== undefined)
  const getPostParent = (page: WxrPageItem): string =>
    homePageId !== null && String(page.parentId ?? '') === homePageId
      ? ROOT_POST_PARENT
      : (getParentPostId(page) ?? ROOT_POST_PARENT)
  const titlesByPageId = getTitlesByPageId(pages)
  const slugsByPageId = getSlugsByPageId(pages, withSeo, getPostParent)

  const header = [
    '<?xml version="1.0" encoding="UTF-8" ?>',
    '<rss version="2.0"',
    '     xmlns:excerpt="http://wordpress.org/export/1.2/excerpt/"',
    '     xmlns:content="http://purl.org/rss/1.0/modules/content/"',
    '     xmlns:wfw="http://wellformedweb.org/CommentAPI/"',
    '     xmlns:dc="http://purl.org/dc/elements/1.1/"',
    '     xmlns:wp="http://wordpress.org/export/1.2/"',
    '>',
    '    <channel>',
    `        <title>${getCdata(projectTitle)}</title>`,
    '        <description></description>',
    `        <pubDate>${pubDate}</pubDate>`,
    '        <language>en-US</language>',
    `        <wp:wxr_version>${WXR_VERSION}</wp:wxr_version>`,
    '',
    '        <wp:author>',
    '            <wp:author_id>1</wp:author_id>',
    '            <wp:author_login><![CDATA[admin]]></wp:author_login>',
    `            <wp:author_email>${getCdata(authorEmail ?? '')}</wp:author_email>`,
    '            <wp:author_display_name><![CDATA[admin]]></wp:author_display_name>',
    '            <wp:author_first_name><![CDATA[]]></wp:author_first_name>',
    '            <wp:author_last_name><![CDATA[]]></wp:author_last_name>',
    '        </wp:author>',
    '',
    '        <generator>https://wordpress.org/?v=6.6</generator>',
  ]

  const items = pages.map((page, index) => {
    const postId = String(index + 1)
    const pageId = String(page.id)
    const postParent = getPostParent(page)
    const title = titlesByPageId.get(pageId) || `Page ${postId}`
    const slug = slugsByPageId.get(pageId) || ''
    const excerpt = getSeoExcerpt(page.notes, withSeo)

    return [
      '<item>',
      `<title>${getCdata(title)}</title>`,
      `<pubDate>${pubDate}</pubDate>`,
      '<dc:creator><![CDATA[admin]]></dc:creator>',
      `<wp:post_id>${postId}</wp:post_id>`,
      '<description/>',
      `<content:encoded>${getCdata(page.content)}</content:encoded>`,
      `<excerpt:encoded>${getCdata(excerpt)}</excerpt:encoded>`,
      `<wp:post_date>${getCdata(pageDate)}</wp:post_date>`,
      `<wp:post_date_gmt>${getCdata(pageDate)}</wp:post_date_gmt>`,
      `<wp:post_modified>${getCdata(pageDate)}</wp:post_modified>`,
      `<wp:post_modified_gmt>${getCdata(pageDate)}</wp:post_modified_gmt>`,
      '<wp:comment_status><![CDATA[closed]]></wp:comment_status>',
      '<wp:ping_status><![CDATA[open]]></wp:ping_status>',
      `<wp:post_name>${getCdata(slug)}</wp:post_name>`,
      '<wp:status><![CDATA[publish]]></wp:status>',
      `<wp:post_parent>${postParent}</wp:post_parent>`,
      '<wp:menu_order>0</wp:menu_order>',
      '<wp:post_type><![CDATA[page]]></wp:post_type>',
      '<wp:post_password><![CDATA[]]></wp:post_password>',
      '<wp:is_sticky>0</wp:is_sticky>',
      getPostMetaXml([
        {
          key: PAGE_TEMPLATE_META_KEY,
          value: PAGE_TEMPLATE_META_VALUE,
        },
        ...getSeoPostMeta(page.notes, withSeo),
      ]),
      '</item>',
    ].join('')
  })

  return [...header, ...items, '</channel>', '</rss>'].join('\n')
}
